<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <span class="text-h5">Gestión de Webhooks</span>
            <v-spacer></v-spacer>
            <v-btn color="info" outlined @click="openLogsDialog" class="mr-2">
              <v-icon left>mdi-text-box-search-outline</v-icon>
              Ver Logs
            </v-btn>
            <v-btn color="primary" @click="openCreateDialog">
              <v-icon left>mdi-plus</v-icon>
              Crear Webhook
            </v-btn>
          </v-card-title>

          <v-card-text>
            <!-- Tabla de webhooks -->
            <v-data-table
              :headers="headers"
              :items="webhooks"
              :loading="loading"
              loading-text="Cargando webhooks..."
              no-data-text="No hay webhooks configurados"
              class="elevation-1"
            >
              <!-- Columna: Nombre -->
              <template v-slot:item.name="{ item }">
                <div>
                  <div class="font-weight-medium">{{ item.name }}</div>
                  <div class="text-caption text--secondary">
                    {{ getWebhookTypeLabel(item.type) }}
                  </div>
                </div>
              </template>

              <!-- Columna: Tipo -->
              <template v-slot:item.type="{ item }">
                <v-chip
                  small
                  :color="getTypeColor(item.type)"
                  text-color="white"
                >
                  {{ getWebhookTypeLabel(item.type) }}
                </v-chip>
              </template>

              <!-- Columna: Evento -->
              <template v-slot:item.event="{ item }">
                <div>
                  <div class="font-weight-medium">{{ item.entity_type }}</div>
                  <div class="text-caption">
                    {{ getEventLabel(item.event) }}
                  </div>
                </div>
              </template>

              <!-- Columna: Estado -->
              <template v-slot:item.enabled="{ item }">
                <v-switch
                  v-model="item.enabled"
                  @change="toggleWebhook(item)"
                  :loading="item.toggling"
                  color="success"
                  dense
                  hide-details
                ></v-switch>
              </template>

              <!-- Columna: Estadísticas -->
              <template v-slot:item.stats="{ item }">
                <div class="d-flex flex-column">
                  <div class="text-caption">
                    <v-icon x-small color="success">mdi-check-circle</v-icon>
                    Total: {{ item.total_triggers || 0 }}
                  </div>
                  <div class="text-caption" v-if="item.failed_triggers > 0">
                    <v-icon x-small color="error">mdi-alert-circle</v-icon>
                    Errores: {{ item.failed_triggers }}
                  </div>
                  <div class="text-caption" v-if="item.last_triggered_at">
                    Último: {{ formatDate(item.last_triggered_at) }}
                  </div>
                </div>
              </template>

              <!-- Columna: Acciones -->
              <template v-slot:item.actions="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      @click="viewWebhookLogs(item)"
                    >
                      <v-icon small>mdi-text-box-search-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Ver logs</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      @click="editWebhook(item)"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Editar</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="error"
                      v-bind="attrs"
                      v-on="on"
                      @click="confirmDelete(item)"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Eliminar</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Diálogo de confirmación de eliminación -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5">
          ¿Eliminar webhook?
        </v-card-title>
        <v-card-text>
          ¿Estás seguro de que deseas eliminar el webhook
          <strong>{{ webhookToDelete?.name }}</strong
          >? Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">
            Cancelar
          </v-btn>
          <v-btn color="error" text @click="deleteWebhook" :loading="deleting">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo de formulario (Create/Edit) -->
    <v-dialog v-model="formDialog" max-width="900" persistent scrollable>
      <webhook-form
        v-if="formDialog"
        :webhook="selectedWebhook"
        :metadata="metadata"
        @close="closeFormDialog"
        @save="handleSave"
      />
    </v-dialog>

    <!-- Diálogo de logs -->
    <v-dialog v-model="logsDialog" max-width="1400" scrollable>
      <webhook-logs
        v-if="logsDialog"
        :webhook-id="selectedWebhookForLogs"
        @close="closeLogsDialog"
      />
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import WebhookForm from "./WebhookForm.vue";
import WebhookLogs from "./WebhookLogs.vue";

export default {
  name: "ConfigWebhooks",
  components: {
    WebhookForm,
    WebhookLogs
  },
  data() {
    return {
      headers: [
        { text: "Nombre", value: "name", sortable: true },
        { text: "Tipo", value: "type", sortable: true },
        { text: "Evento", value: "event", sortable: true },
        { text: "Activo", value: "enabled", sortable: false, align: "center" },
        { text: "Estadísticas", value: "stats", sortable: false },
        { text: "Acciones", value: "actions", sortable: false, align: "center" }
      ],
      formDialog: false,
      deleteDialog: false,
      logsDialog: false,
      selectedWebhook: null,
      webhookToDelete: null,
      selectedWebhookForLogs: null,
      deleting: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      realEstateGroupId: localStorage.getItem("ag")
    };
  },
  computed: {
    ...mapState("webhooks", ["webhooks", "metadata", "loading"])
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    ...mapActions("webhooks", [
      "fetchMetadata",
      "fetchWebhooks",
      "updateWebhook",
      "deleteWebhook"
    ]),
    async loadData() {
      try {
        await Promise.all([
          this.fetchMetadata({ realEstateGroupId: this.realEstateGroupId }),
          this.fetchWebhooks()
        ]);
      } catch (error) {
        this.showSnackbar("Error al cargar los datos", "error");
      }
    },
    openCreateDialog() {
      this.selectedWebhook = null;
      this.formDialog = true;
    },
    editWebhook(webhook) {
      this.selectedWebhook = { ...webhook };
      this.formDialog = true;
    },
    closeFormDialog() {
      this.formDialog = false;
      this.selectedWebhook = null;
    },
    async handleSave() {
      this.closeFormDialog();
      await this.fetchWebhooks();
      this.showSnackbar("Webhook guardado correctamente", "success");
    },
    async toggleWebhook(webhook) {
      try {
        this.$set(webhook, "toggling", true);
        await this.updateWebhook({
          id: webhook._id,
          webhookData: { enabled: webhook.enabled }
        });
        this.showSnackbar(
          webhook.enabled
            ? "Webhook activado correctamente"
            : "Webhook desactivado correctamente",
          "success"
        );
      } catch (error) {
        webhook.enabled = !webhook.enabled;
        this.showSnackbar("Error al cambiar el estado del webhook", "error");
      } finally {
        this.$set(webhook, "toggling", false);
      }
    },
    confirmDelete(webhook) {
      this.webhookToDelete = webhook;
      this.deleteDialog = true;
    },
    async deleteWebhook() {
      try {
        this.deleting = true;
        await this.$store.dispatch(
          "webhooks/deleteWebhook",
          this.webhookToDelete._id
        );
        this.deleteDialog = false;
        this.webhookToDelete = null;
        this.showSnackbar("Webhook eliminado correctamente", "success");
      } catch (error) {
        this.showSnackbar("Error al eliminar el webhook", "error");
      } finally {
        this.deleting = false;
      }
    },
    getWebhookTypeLabel(type) {
      const typeConfig = this.metadata?.webhook_types?.find(
        t => t.type === type
      );
      return typeConfig?.ui?.label || type;
    },
    getEventLabel(event) {
      if (!this.metadata?.entities) return event;

      for (const entity of this.metadata.entities) {
        const eventConfig = entity.events?.find(e => e.event_action === event);
        if (eventConfig) {
          return eventConfig.ui?.label || event;
        }
      }
      return event;
    },
    getTypeColor(type) {
      const colors = {
        meta_conversions: "blue",
        generic: "blue",
        zapier: "orange",
        custom: "teal"
      };
      return colors[type] || "grey";
    },
    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      return d.toLocaleDateString("es-ES", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    showSnackbar(text, color = "success") {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    openLogsDialog() {
      this.selectedWebhookForLogs = null;
      this.logsDialog = true;
    },
    viewWebhookLogs(webhook) {
      this.selectedWebhookForLogs = webhook._id;
      this.logsDialog = true;
    },
    closeLogsDialog() {
      this.logsDialog = false;
      this.selectedWebhookForLogs = null;
    }
  }
};
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper {
  max-height: calc(100vh - 300px);
}
</style>
